<template>
  <div>
    <b-button variant="primary" @click="openCreateModal">
      <i class="fas fa-plus"></i> New Banner
    </b-button>

    <b-modal
      id="banner-modal"
      ref="banner-modal"
      :title="isEdit ? 'Edit Banner' : 'Create New Banner'"
      size="lg"
      @hidden="resetForm"
      @ok="handleSubmit"
    >
      <b-form @submit.prevent="handleSubmit">
        <b-form-group label="Banner Type *" label-for="banner-type">
          <b-form-select
            id="banner-type"
            v-model="form.bannerType"
            :options="bannerTypeOptions"
            required
            :disabled="isEdit"
          ></b-form-select>
        </b-form-group>

        <b-form-group label="Banner Image *" label-for="banner-image">
          <b-form-file
            id="banner-image"
            v-model="form.imageFile"
            accept="image/*"
            placeholder="Choose image file..."
            @change="onImageChange"
          ></b-form-file>

          <div v-if="imagePreview" class="mt-2">
            <img
              :src="imagePreview"
              alt="Preview"
              style="max-width: 200px; max-height: 200px"
              class="img-thumbnail"
            />
          </div>
        </b-form-group>

        <div v-if="form.bannerType === 1">
          <b-form-group label="Author Name *" label-for="author-name">
            <b-form-input
              id="author-name"
              v-model="form.name"
              placeholder="Enter author name"
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group
            label="Author Designation *"
            label-for="author-designation"
          >
            <b-form-input
              id="author-designation"
              v-model="form.designation"
              placeholder="Enter author designation"
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Quote *" label-for="author-quote">
            <b-form-textarea
              id="author-quote"
              v-model="form.quote"
              placeholder="Enter quote text"
              rows="3"
              required
            ></b-form-textarea>
          </b-form-group>
        </div>

        <div v-if="form.bannerType === 2">
          <b-form-group label="Redirect URL" label-for="banner-url">
            <b-form-input
              id="banner-url"
              v-model="form.url"
              placeholder="Enter redirect URL (optional)"
              type="url"
            ></b-form-input>
          </b-form-group>
        </div>

        <b-form-group>
          <b-form-checkbox v-model="form.active" name="active-status">
            Active Banner
          </b-form-checkbox>
        </b-form-group>
      </b-form>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()"> Cancel </b-button>
        <b-button
          variant="primary"
          @click="handleSubmit"
          :disabled="isSubmitting"
        >
          <b-spinner small v-if="isSubmitting"></b-spinner>
          {{ isSubmitting ? "Saving..." : isEdit ? "Update" : "Create" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: "BannerCreateModal",
  props: {
    editBanner: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isSubmitting: false,
      imagePreview: null,
      form: {
        bannerType: 1,
        imageFile: null,
        name: "",
        designation: "",
        quote: "",
        url: "",
        active: true,
      },
      bannerTypeOptions: [
        { value: 1, text: "Quote Banner" },
        { value: 2, text: "Announcement Banner" },
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.editBanner;
    },
  },
  watch: {
    editBanner: {
      handler(banner) {
        if (banner) {
          this.populateForm(banner);
          this.$refs["banner-modal"].show();
        }
      },
      immediate: true,
    },
    "form.bannerType"() {
      this.form.name = "";
      this.form.designation = "";
      this.form.quote = "";
    },
  },
  methods: {
    openCreateModal() {
      this.resetForm();
      this.$refs["banner-modal"].show();
    },

    populateForm(banner) {
      this.form.bannerType = banner.bannerType;
      this.form.name = banner.name || "";
      this.form.designation = banner.designation || "";
      this.form.quote = banner.quote || "";
      this.form.url = banner.url || "";
      this.form.active = banner.active;

      if (banner.image) {
        this.imagePreview = banner.image;
      }
    },

    onImageChange(file) {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const formData = new FormData();
        formData.append("bannerType", this.form.bannerType);
        formData.append("active", this.form.active);

        if (this.form.imageFile) {
          formData.append("image", this.form.imageFile);
        }

        if (this.form.bannerType === 1) {
          formData.append("name", this.form.name);
          formData.append("designation", this.form.designation);
          formData.append("quote", this.form.quote);
        } else if (this.form.bannerType === 2) {
          if (this.form.url) {
            formData.append("url", this.form.url);
          }
        }

        let response;
   
        if (this.isEdit) {
          response = await this.$apiV2.put(
            `announcement-banner/${this.editBanner._id}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
        } else {
          response = await this.$apiV2.post("announcement-banner", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
        if (response.data.success) {
          this.$bvToast.toast(
            response.data.message ||
              (this.isEdit
                ? "Banner updated successfully"
                : "Banner created successfully"),
            {
              title: "Success",
              variant: "success",
              solid: true,
            }
          );
          this.$refs["banner-modal"].hide();
          this.$emit("bannerSaved");
        } else {
          this.$bvToast.toast(
            response.data.message || "Failed to save banner",
            {
              title: "Error",
              variant: "danger",
              solid: true,
            }
          );
        }
      } catch (error) {
        this.$bvToast.toast(
          error.response?.data?.message ||
            "An error occurred while saving banner",
          {
            title: "Error",
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.isSubmitting = false;
      }
    },

    validateForm() {
      // Basic validation
      if (!this.form.bannerType) {
        this.$bvToast.toast("Please select banner type", {
          title: "Validation Error",
          variant: "danger",
          solid: true,
        });
        return false;
      }

      if (!this.isEdit && !this.form.imageFile) {
        this.$bvToast.toast("Please select an image", {
          title: "Validation Error",
          variant: "danger",
          solid: true,
        });
        return false;
      }

      if (this.form.bannerType === 1) {
        if (!this.form.name?.trim()) {
          this.$bvToast.toast("Author name is required for quote banner", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return false;
        }
        if (!this.form.designation?.trim()) {
          this.$bvToast.toast(
            "Author designation is required for quote banner",
            {
              title: "Validation Error",
              variant: "danger",
              solid: true,
            }
          );
          return false;
        }
        if (!this.form.quote?.trim()) {
          this.$bvToast.toast("Quote is required for quote banner", {
            title: "Validation Error",
            variant: "danger",
            solid: true,
          });
          return false;
        }
      }

      return true;
    },
    resetForm() {
      this.form = {
        bannerType: 1,
        imageFile: null,
        name: "",
        designation: "",
        quote: "",
        url: "",
        active: true,
      };
      this.imagePreview = null;
      this.isSubmitting = false;
      this.$emit("resetEdit");
    },
  },
};
</script>

<style scoped>
.img-thumbnail {
  border-radius: 8px;
}
</style>
